import { ApplicationFeeStatus, AppliedDiscountInfo, Money } from "shared/types";
import { formatToCurrency } from "shared/utils";
import { formatTitleFirstWord } from "utils/formatTitle";
import { cx } from "utils/classnames";
import { Check } from "untitledui-js-base";
import { Cell } from "../Cell";
import { ApplicantOrInvitee } from "../../types";
import { Applicant } from "types/application";
import {
  CashbackIcon,
  MinusSignIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { DiscountStatus } from "views/discounts/components/DiscountStatus";

type ExtendedDiscountInfo = AppliedDiscountInfo & {
  final_amount?: Money;
};

type ApplicantWithDiscount = Applicant & {
  discount_info?: ExtendedDiscountInfo;
};

const iconMap = {
  [ApplicationFeeStatus.Paid]: (
    <Check size="20" className="stroke-success-600" />
  ),
  [ApplicationFeeStatus.Refunded]: (
    <HugeiconsIcon icon={CashbackIcon} size="20" className="text-warning-500" />
  ),
  [ApplicationFeeStatus.Unpaid]: (
    <HugeiconsIcon
      icon={MinusSignIcon}
      size={20}
      className="text-content-100"
    />
  )
};

export const ApplicationFeeCell = ({
  applicant: applicantOrInvitee,
  setDiscountModal,
  handleCancelDiscount,
  isActiveDiscounts,
  alwaysAllowCustomDiscounts = false
}: {
  applicant: ApplicantOrInvitee | ApplicantWithDiscount;
  setDiscountModal: (v: { email: string; id: number } | null) => void;
  handleCancelDiscount: (applicantId: number) => void;
  isActiveDiscounts: boolean;
  alwaysAllowCustomDiscounts?: boolean;
}) => {
  const isInvitee = "is_public" in applicantOrInvitee;

  if (
    isInvitee ||
    applicantOrInvitee.application_fee_status ===
      ApplicationFeeStatus.NoApplicationFee
  )
    return (
      <Cell isFirst isDataCell className="!h-[100px] text-content-400">
        <HugeiconsIcon
          icon={MinusSignIcon}
          size={20}
          className="text-content-100"
        />
      </Cell>
    );

  const applicantWithDiscount = applicantOrInvitee as ApplicantWithDiscount;

  if (applicantWithDiscount.application_fee_status === null)
    return (
      <Cell isFirst isDataCell className="!h-[100px]">
        <span className="text-content-200">N/A</span>
      </Cell>
    );

  const applicationFeeStatusItem = applicantOrInvitee.fee_statuses?.find(
    (fee) => fee.fee_type === "application_fee"
  );
  // Prefer fee_status data if it exists, otherwise fallback to applicant
  const applicationStatusFee = applicationFeeStatusItem
      ? applicationFeeStatusItem.status
      : applicantWithDiscount.application_fee_status,
    discountInfo = applicantWithDiscount.discount_info ?? null,
    applicationFee = applicantWithDiscount.application_fee ?? null,
    refundedApplicationFee = applicationFeeStatusItem
      ? applicationFeeStatusItem.refunded_amount
      : (applicantWithDiscount.refunded_application_fee ?? null);

  return (
    <Cell isFirst isDataCell className="!h-[100px] flex-col justify-between">
      <div className="flex gap-2 items-center mb-4">
        {iconMap[applicationStatusFee]}
        <p
          className={cx(
            "font-medium",
            applicantWithDiscount.application_fee_status ===
              ApplicationFeeStatus.Unpaid
              ? "text-content-200"
              : "text-content-1000"
          )}
        >
          {formatTitleFirstWord(applicantWithDiscount.application_fee_status)}
        </p>
        <p
          className={cx(
            applicantWithDiscount.application_fee_status ===
              ApplicationFeeStatus.Unpaid
              ? "text-content-200"
              : "text-content-300"
          )}
        >
          {discountInfo?.discount_applied ? (
            <>
              (
              <span>
                {applicationStatusFee === ApplicationFeeStatus.Refunded &&
                  `${formatToCurrency(refundedApplicationFee, {
                    minimumFractionDigits: 2
                  })} of `}
              </span>
              <span className="line-through">
                {formatToCurrency(
                  applicationStatusFee === ApplicationFeeStatus.Paid &&
                    applicationFee &&
                    discountInfo.discount_amount
                    ? {
                        cents:
                          applicationFee.cents +
                          (typeof discountInfo.discount_amount === "number"
                            ? discountInfo.discount_amount
                            : discountInfo.discount_amount.cents),
                        currency: applicationFee.currency
                      }
                    : applicationFee,
                  { minimumFractionDigits: 2 }
                )}
              </span>{" "}
              {formatToCurrency(
                applicationStatusFee === ApplicationFeeStatus.Paid
                  ? applicationFee
                  : (discountInfo.final_amount ?? null),
                { minimumFractionDigits: 2 }
              )}
              )
            </>
          ) : (
            <>
              (
              <span>
                {applicationStatusFee === ApplicationFeeStatus.Refunded &&
                  `${formatToCurrency(refundedApplicationFee, {
                    minimumFractionDigits: 2
                  })} of `}
              </span>
              <span>
                {formatToCurrency(applicationFee, { minimumFractionDigits: 2 })}
                )
              </span>
            </>
          )}
        </p>
      </div>
      <div className="flex gap-2 items-center w-full">
        <DiscountStatus
          applicant={{
            id: applicantOrInvitee.id!,
            application_fee_status: applicantOrInvitee.application_fee_status,
            application_status: applicantOrInvitee.application_status,
            discount_info: applicantOrInvitee.discount_info
          }}
          onApplyDiscountClick={() => {
            setDiscountModal({
              email: applicantOrInvitee.email,
              id: applicantOrInvitee.id!
            });
          }}
          onCancelDiscountClick={handleCancelDiscount}
          isActiveDiscounts={isActiveDiscounts}
          alwaysAllowCustomDiscounts={alwaysAllowCustomDiscounts}
        />
      </div>
    </Cell>
  );
};
